/**
 * Notification Preferences Modal
 * 
 * Settings dialog for managing notification preferences.
 * Allows users to toggle notification types and choose toast vs persistent display.
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useNotifications } from "./use-notifications";
import { toast } from "sonner";

interface PreferenceRowProps {
  label: string;
  description: string;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
}

const PreferenceRow = ({
  label,
  description,
  enabled,
  onEnabledChange,
}: PreferenceRowProps) => {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="space-y-0.5 flex-1">
        <Label className="text-sm font-medium">{label}</Label>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch checked={enabled} onCheckedChange={onEnabledChange} />
    </div>
  );
};


export const NotificationPreferencesModal = () => {
  const {
    isPreferencesModalOpen,
    closePreferencesModal,
    preferences,
    updatePreferences,
  } = useNotifications();
  
  // Local state for form
  const [formData, setFormData] = useState({
    order_filled_enabled: true,
    tp_sl_enabled: true,
    liquidation_enabled: true,
    deposit_enabled: true,
    withdrawal_enabled: true,
    near_liquidation_enabled: true,
    // Global toast setting - applies to all notifications
    globalToastEnabled: true,
  });
  
  // Update form when preferences load
  useEffect(() => {
    if (preferences) {
      // Determine global toast setting (if ANY toast is enabled, global is true)
      const anyToastEnabled = preferences.order_filled_toast || 
                              preferences.tp_sl_toast || 
                              preferences.deposit_toast || 
                              preferences.withdrawal_toast;
      
      setFormData({
        order_filled_enabled: preferences.order_filled_enabled,
        tp_sl_enabled: preferences.tp_sl_enabled,
        liquidation_enabled: preferences.liquidation_enabled,
        deposit_enabled: preferences.deposit_enabled,
        withdrawal_enabled: preferences.withdrawal_enabled,
        near_liquidation_enabled: preferences.near_liquidation_enabled,
        globalToastEnabled: anyToastEnabled,
      });
    }
  }, [preferences]);
  
  const handleSave = async () => {
    try {
      // Convert global toast setting to individual toast flags
      const prefs = {
        order_filled_enabled: formData.order_filled_enabled,
        order_filled_toast: formData.globalToastEnabled,
        tp_sl_enabled: formData.tp_sl_enabled,
        tp_sl_toast: formData.globalToastEnabled,
        liquidation_enabled: formData.liquidation_enabled,
        liquidation_toast: false, // Liquidation always persistent (too important)
        deposit_enabled: formData.deposit_enabled,
        deposit_toast: formData.globalToastEnabled,
        withdrawal_enabled: formData.withdrawal_enabled,
        withdrawal_toast: formData.globalToastEnabled,
        near_liquidation_enabled: formData.near_liquidation_enabled,
        near_liquidation_toast: false, // Near liquidation always persistent (too important)
      };
      
      await updatePreferences(prefs);
      toast.success("Preferences saved successfully");
      closePreferencesModal();
    } catch (error) {
      toast.error("Failed to save preferences");
    }
  };
  
  return (
    <Dialog open={isPreferencesModalOpen} onOpenChange={closePreferencesModal}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Notification Preferences</DialogTitle>
          <DialogDescription>
            Choose which notifications you want to receive. Enable notification pop-ups for quick auto-dismiss alerts.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Global Toast Setting */}
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1 flex-1">
                <Label className="text-sm font-medium">Display as Notification Pop-ups</Label>
                <p className="text-xs text-muted-foreground">
                  {formData.globalToastEnabled 
                    ? "Auto-dismiss after a few seconds (except critical alerts)" 
                    : "Notifications stay until manually dismissed"}
                </p>
              </div>
              <Switch 
                checked={formData.globalToastEnabled} 
                onCheckedChange={(enabled) =>
                  setFormData({ ...formData, globalToastEnabled: enabled })
                }
              />
            </div>
          </div>
          
          <Separator />
          
          <div className="space-y-0">
            {/* Order Filled */}
            <PreferenceRow
              label="Order Filled"
              description="When your limit orders are executed"
              enabled={formData.order_filled_enabled}
              onEnabledChange={(enabled) =>
                setFormData({ ...formData, order_filled_enabled: enabled })
              }
            />
            
            <Separator />
            
            {/* TP/SL Triggered */}
            <PreferenceRow
              label="Take Profit / Stop Loss"
              description="When your TP/SL orders are triggered"
              enabled={formData.tp_sl_enabled}
              onEnabledChange={(enabled) =>
                setFormData({ ...formData, tp_sl_enabled: enabled })
              }
            />
            
            <Separator />
            
            {/* Liquidation */}
            <PreferenceRow
              label="Liquidation"
              description="When your position is liquidated"
              enabled={formData.liquidation_enabled}
              onEnabledChange={(enabled) =>
                setFormData({ ...formData, liquidation_enabled: enabled })
              }
            />
            
            <Separator />
            
            {/* Deposit */}
            <PreferenceRow
              label="Deposit"
              description="When deposits are received and confirmed"
              enabled={formData.deposit_enabled}
              onEnabledChange={(enabled) =>
                setFormData({ ...formData, deposit_enabled: enabled })
              }
            />
            
            <Separator />
            
            {/* Withdrawal */}
            <PreferenceRow
              label="Withdrawal"
              description="When withdrawal status changes"
              enabled={formData.withdrawal_enabled}
              onEnabledChange={(enabled) =>
                setFormData({ ...formData, withdrawal_enabled: enabled })
              }
            />
            
            <Separator />
            
            {/* Near Liquidation */}
            <PreferenceRow
              label="Near Liquidation Warning"
              description="When your position is approaching liquidation"
              enabled={formData.near_liquidation_enabled}
              onEnabledChange={(enabled) =>
                setFormData({ ...formData, near_liquidation_enabled: enabled })
              }
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={closePreferencesModal}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            className="bg-bid-foreground hover:bg-bid-foreground/90 text-white"
          >
            Save Preferences
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

